// File: src/components/CategoryRowSection.jsx
// Single category row displaying multiple subcategory cards
// Amazon-style horizontal category section
// Part of AllCategoriesSection hierarchy

import React from 'react';
import { Link } from 'react-router-dom';
import SubcategoryCard from './SubcategoryCard';

const CategoryRowSection = React.memo(({ category }) => {
  // Validate category has required data
  if (!category || !category.subcategories || category.subcategories.length === 0) {
    return null;
  }

  return (
    <section className="mb-12 last:mb-0">
      {/* Category Header with See All */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">
            {category.name}
          </h2>
          <div className="h-1 w-16 bg-blue-600 rounded-full"></div>
        </div>
        
        <Link
          to={`/category/${category.slug}`}
          className="text-sm md:text-base font-semibold text-blue-600 hover:text-blue-700 transition-colors duration-200 inline-flex items-center gap-2 group"
        >
          See all
          <span className="text-lg transition-transform duration-200 group-hover:translate-x-1">›</span>
        </Link>
      </div>

      {/* Responsive Subcategory Cards Grid */}
      
      {/* Desktop: 4 cards per row */}
      <div className="hidden lg:grid grid-cols-4 gap-6">
        {category.subcategories.map((subcategory) => (
          <SubcategoryCard
            key={subcategory.subCategoryId}
            subcategory={subcategory}
          />
        ))}
      </div>

      {/* Tablet: 2 cards per row */}
      <div className="hidden md:grid lg:hidden grid-cols-2 gap-6">
        {category.subcategories.map((subcategory) => (
          <SubcategoryCard
            key={subcategory.subCategoryId}
            subcategory={subcategory}
          />
        ))}
      </div>

      {/* Mobile: Horizontal scroll for better UX on small screens */}
      <div className="md:hidden">
        <div className="flex gap-4 overflow-x-auto pb-4 -mx-4 px-4 snap-x snap-mandatory scrollbar-hide">
          {category.subcategories.map((subcategory) => (
            <div 
              key={subcategory.subCategoryId} 
              className="flex-shrink-0 w-[280px] snap-start"
            >
              <SubcategoryCard subcategory={subcategory} />
            </div>
          ))}
        </div>
        
        {/* Mobile scroll indicator */}
        <div className="flex justify-center mt-2 gap-1">
          {category.subcategories.slice(0, 5).map((_, index) => (
            <div 
              key={index} 
              className="w-2 h-2 rounded-full bg-gray-300"
            />
          ))}
          {category.subcategories.length > 5 && (
            <span className="text-xs text-gray-500 ml-2">
              +{category.subcategories.length - 5} more
            </span>
          )}
        </div>
      </div>
    </section>
  );
});

CategoryRowSection.displayName = 'CategoryRowSection';

export default CategoryRowSection;
